// CartSummary — cart totals: subtotal, tax estimate, promo discount, total.
// Inline styles only — no Tailwind. Themed via useEmbedTokens().

import { useEmbedTokens } from "../../embed-shared/primitives";
import type { TokenSet } from "../../embed-shared/tokens";
import { formatCurrency } from "../../embed-shared/currency";
import { TAX_AT_BILLING_VALUE, TOTAL_BEFORE_TAX_LABEL, isTaxPending } from "../../embed-shared/tax-display";
import { useStorefrontConfig } from "./storefront-config";

export interface CartSummaryProps {
  subtotal: number;
  taxEstimate: number;
  promoDiscount: number;
  shipping: number;
  total: number;
  promoCode?: string | null;
}

function SummaryRow({
  t,
  label,
  amount,
  text,
  currency,
  bold,
  color,
}: {
  t: TokenSet;
  label: string;
  /** Rendered instead of a formatted amount (e.g. "Calculated at billing"). */
  text?: string;
  amount?: number;
  currency: string;
  bold?: boolean;
  color?: string;
}) {
  const cellStyle = {
    fontSize: bold ? t.fontSize.md : t.fontSize.sm,
    fontWeight: bold ? 700 : 400,
    // Mock .summary-row: regular rows read as secondary text; only the total
    // row (bold) carries primary text weight.
    color: color ?? (bold ? t.colors.text : t.colors.textSecondary),
  };
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: `${t.spacing.xs}px 0`,
      }}
    >
      <span style={cellStyle}>{label}</span>
      <span style={{ ...cellStyle, fontVariantNumeric: "tabular-nums" }}>
        {text ?? ((amount ?? 0) < 0 ? `−${formatCurrency(Math.abs(amount ?? 0), currency)}` : formatCurrency(amount ?? 0, currency))}
      </span>
    </div>
  );
}

export function CartSummary({
  subtotal,
  taxEstimate,
  promoDiscount,
  shipping,
  total,
  promoCode,
}: CartSummaryProps) {
  const t = useEmbedTokens();
  // TD-308: shipping=$0 label is operator-configurable (default "$0.00" since
  // migration 144). The
  // hook returns the default when no provider is mounted (isolated unit tests).
  // TD-521: display currency is the marketplace-wide settings value — this
  // component dropped its currency prop (fed by a never-populated cart.currency)
  // in favour of the same context every other money format reads.
  const { currency, freeShippingLabel } = useStorefrontConfig();
  return (
    <div
      style={{
        // Operator directive 2026-08-16 (Apple-style cart): totals present as
        // a soft card rather than floating rows under a bare top border.
        border: `1px solid ${t.colors.cardBorder}`,
        borderRadius: t.borderRadius,
        backgroundColor: t.colors.cardBg,
        padding: t.spacing.lg,
      }}
    >
      <SummaryRow t={t} label="Subtotal" amount={subtotal} currency={currency} />
      {isTaxPending(taxEstimate) ? (
        <SummaryRow t={t} label="Tax" text={TAX_AT_BILLING_VALUE} currency={currency} />
      ) : (
        <SummaryRow t={t} label="Tax (estimate)" amount={taxEstimate} currency={currency} />
      )}
      {shipping === 0 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: `${t.spacing.xs}px 0`,
          }}
        >
          <span style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary }}>Shipping</span>
          {/* D6: the $0 value is a price in a column of prices — neutral text,
              not the success green (a green $0.00 reads as a promotion). */}
          <span style={{ fontSize: t.fontSize.sm, color: t.colors.text, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{freeShippingLabel}</span>
        </div>
      ) : (
        <SummaryRow t={t} label="Shipping" amount={shipping} currency={currency} />
      )}
      {promoDiscount > 0 && (
        <SummaryRow
          t={t}
          label={promoCode ? `Promo (${promoCode})` : "Promo discount"}
          amount={-promoDiscount}
          currency={currency}
          color={t.colors.successGreen}
        />
      )}
      <div
        style={{
          borderTop: `1px solid ${t.colors.cardBorder}`,
          marginTop: t.spacing.sm,
          paddingTop: t.spacing.sm,
        }}
      >
        <SummaryRow t={t} label={isTaxPending(taxEstimate) ? TOTAL_BEFORE_TAX_LABEL : "Total"} amount={total} currency={currency} bold />
      </div>
    </div>
  );
}
